"use client";

import Link from "next/link";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  FilePlus2,
  Plus,
  RefreshCw,
  Search
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "../../components/ui/sheet";
import { Skeleton } from "../../components/ui/skeleton";
import { useUnsavedWork } from "../../components/desk/unsaved-work";
import { RequireRole } from "../../components/console-shell";
import {
  Banner,
  Button,
  ButtonRow,
  CheckList,
  ConfirmPanel,
  Desk,
  DeskCard,
  Fact,
  Facts,
  Field,
  FieldGrid,
  FormSection,
  Input,
  Note,
  RecordList,
  RecordRow,
  SelectField,
  StatusTag,
  Textarea
} from "../../components/desk";
import { api, commandId, formatTime, reasonText } from "../../lib/api";
import { scopeOrganizationIds, useSession } from "../../lib/session";

import { ConfirmedCommand } from "../../components/desk/confirmed-command";
import { ActivityArtifacts } from "../../components/desk/activity-artifacts";
import { ActivityCloseout } from "../../components/desk/activity-closeout";
import type { ArchiveActivity } from "../../lib/activity-ledger-api";
import { ActivityReschedule } from "../../components/desk/activity-reschedule";

type ActivityStatus = "DRAFT" | "PUBLISHED" | "CANCELLED" | "CLOSED";

type OrgNode = {
  id: string;
  name: string;
  parentId: string | null;
  kind: { code: string; canHoldMembership: boolean } | null;
  children: OrgNode[];
};

function flattenOrgs(nodes: OrgNode[]): OrgNode[] {
  return nodes.flatMap((node) => [node, ...flattenOrgs(node.children ?? [])]);
}

/** 发起组织自己 + 它的全部下级。受众只能在这里面选（ADR-0030 §3.3）。 */
function subtreeOf(all: OrgNode[], rootId: string): OrgNode[] {
  const byParent = new Map<string | null, OrgNode[]>();
  for (const node of all) {
    byParent.set(node.parentId, [...(byParent.get(node.parentId) ?? []), node]);
  }
  const root = all.find((node) => node.id === rootId);
  if (!root) return [];
  const out: OrgNode[] = [];
  const walk = (node: OrgNode) => {
    out.push(node);
    for (const child of byParent.get(node.id) ?? []) walk(child);
  };
  walk(root);
  return out;
}

type ActivitySummary = {
  id: string;
  publicRef: string;
  status: ActivityStatus;
  version: number;
  title: string;
  category: string;
  startAt: string;
  endAt: string;
  location: string;
  organizerLabel: string;
  capacity: number;
  enrollmentMode?: "OPEN" | "DELEGATED_QUOTA";
  registeredCount: number;
  waitlistedCount: number;
  excusedCount: number;
  attendedCount: number;
  audienceCount: number;
  /** 我有权的那几格里还空着多少席（WO-47）。 */
  pendingSeats?: number;
  organization: { id: string; name: string };
  updatedAt: string;
};

type QuotaCell = {
  organizationId: string;
  organizationName: string;
  seats: number;
  nominated: number;
  excused: number;
};

type Nominatable = {
  items: Array<{
    personId: string;
    displayName: string;
    status: "NONE" | "REGISTERED" | "EXCUSED" | "CANCELLED";
  }>;
  seats: number;
  nominated: number;
};

type ActivityDetail = ActivitySummary & ArchiveActivity & {
  quotas: QuotaCell[];
  summary: string;
  body: string[];
  notes: string[];
  contactLabel: string;
  enrollOpensAt: string;
  enrollClosesAt: string;
  checkInCode: string;
  checkInMode?: "SELF" | "ON_SITE_CODE";
  cancelReason?: string;
  roster: Array<{
    personId: string;
    displayName: string;
    status: "REGISTERED" | "WAITLISTED" | "EXCUSED" | "CANCELLED";
    registeredAt: string;
    excuseReason?: string;
    checkedInAt?: string;
  }>;
};

const STATUS_LABEL: Record<ActivityStatus, string> = {
  DRAFT: "草稿",
  PUBLISHED: "已发布",
  CANCELLED: "已取消",
  CLOSED: "已结项"
};

// 草稿是待办（还没发布），所以是 warning 不是 neutral；
// 已取消是终态不是事故——DESIGN.md §2.2：danger 只留给真实风险。
const STATUS_TONE: Record<ActivityStatus, "neutral" | "success" | "warning"> = {
  DRAFT: "warning",
  PUBLISHED: "success",
  CANCELLED: "neutral",
  CLOSED: "neutral"
};

const ROSTER_LABEL: Record<string, string> = {
  REGISTERED: "已报名",
  WAITLISTED: "候补",
  EXCUSED: "已请假",
  CANCELLED: "已取消"
};

const ROSTER_TONE: Record<string, "brand" | "neutral" | "warning"> = {
  REGISTERED: "brand",
  WAITLISTED: "neutral",
  EXCUSED: "warning",
  CANCELLED: "neutral"
};

// datetime-local 给出的是不带时区的本地时间；服务端要 ISO。
function toIso(value: string): string {
  return new Date(value).toISOString();
}

function defaultWindow() {
  const start = new Date(Date.now() + 7 * 24 * 3600 * 1000);
  const local = (date: Date) =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  return {
    startAt: local(start),
    endAt: local(new Date(start.getTime() + 2 * 3600 * 1000)),
    enrollOpensAt: local(new Date()),
    enrollClosesAt: local(new Date(start.getTime() - 24 * 3600 * 1000))
  };
}

const SECTIONS = [
  { id: "1", label: "承办组织" },
  { id: "2", label: "受众范围" },
  { id: "3", label: "报名设置" },
  { id: "4", label: "活动内容" },
  { id: "5", label: "活动时间" }
];

export default function ActivitiesPage() {
  return (
    <RequireRole roleCode="ACTIVITY_COORDINATOR">
      <Suspense
        fallback={
          <Desk>
            <p role="status" className="text-sm text-muted-foreground">
              正在准备活动工作区…
            </p>
          </Desk>
        }
      >
        <ActivityDesk />
      </Suspense>
    </RequireRole>
  );
}

function ActivityDesk() {
  const params = useSearchParams();
  const { whoami, hasRole } = useSession();
  const [items, setItems] = useState<ActivitySummary[]>([]);

  const [rescheduling, setRescheduling] = useState(false);
  const [selected, setSelected] = useState<ActivityDetail | null>(null);
  const [filter, setFilter] = useState<ActivityStatus | "">(() => {
    const value = params.get("status");
    return value === "DRAFT" || value === "CANCELLED" || value === "CLOSED" || value === "" ? value : "PUBLISHED";
  });
  const [creating, setCreating] = useState(() => params.get("mode") === "create");
  const [keyword, setKeyword] = useState(() => params.get("q") ?? "");
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [readAt, setReadAt] = useState("");
  const [detailId, setDetailId] = useState<string | null>(() =>
    params.get("mode") === "create" ? null : params.get("activity")
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [orgError, setOrgError] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeSection, setActiveSection] = useState("1");
  const { requestLeave, dialog } = useUnsavedWork(creating && dirty);
  const listRequest = useRef(0);
  const detailRequest = useRef(0);
  const opener = useRef<HTMLElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const draftCommand = useRef<{ fingerprint: string; id: string } | null>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [orgs, setOrgs] = useState<OrgNode[]>([]);
  const [audienceBasis, setAudienceBasis] = useState<"MEMBERSHIP_TREE" | "APPOINTMENT_BODY">(
    "MEMBERSHIP_TREE"
  );
  const [audienceIds, setAudienceIds] = useState<string[]>([]);
  const [enrollmentMode, setEnrollmentMode] = useState<"OPEN" | "DELEGATED_QUOTA">("OPEN");
  const enrollClosesEdited = useRef(false);
  const openEnrollClosesAt = useRef<string | null>(null);
  const [seats, setSeats] = useState<Record<string, number>>({});
  const [quotaOrg, setQuotaOrg] = useState("");
  const [pool, setPool] = useState<Nominatable | null>(null);
  const [pick, setPick] = useState("");
  const [form, setForm] = useState({
    title: "",
    category: "会议",
    summary: "",
    body: "",
    location: "",
    organizerLabel: "市委会办公室",
    contactLabel: "",
    capacity: 40,
    agenda: "",
    notes: "",
    ...defaultWindow()
  });
  const [message, setMessage] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const scopes = scopeOrganizationIds(whoami);

  const load = useCallback(
    async (status: ActivityStatus | "" = filter) => {
      const request = ++listRequest.current;
      setListLoading(true);
      setListError(null);
      try {
        const query = status ? `?status=${encodeURIComponent(status)}` : "";
        const result = await api<{ items: ActivitySummary[] }>(`/api/v1/activities${query}`);
        if (request !== listRequest.current) return;
        setItems(result.items);
        setReadAt(formatTime(new Date().toISOString()));
      } catch (caught: unknown) {
        if (request !== listRequest.current) return;
        setItems([]);
        setReadAt("");
        setListError(reasonText(caught));
      } finally {
        if (request === listRequest.current) setListLoading(false);
      }
    },
    [filter]
  );

  useEffect(() => {
    void load();
    return () => {
      ++listRequest.current;
    };
  }, [load]);
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", filter);
    if (keyword) url.searchParams.set("q", keyword);
    else url.searchParams.delete("q");
    if (detailId) url.searchParams.set("activity", detailId);
    else url.searchParams.delete("activity");
    if (creating) url.searchParams.set("mode", "create");
    else url.searchParams.delete("mode");
    window.history.replaceState(window.history.state, "", url);
  }, [filter, keyword, detailId, creating]);

  useEffect(() => {
    if (!organizationId && scopes.length === 1) setOrganizationId(scopes[0]!);
  }, [organizationId, scopes]);

  const loadOrgs = useCallback(async () => {
    setOrgError(false);
    try {
      const result = await api<{ items: OrgNode[] }>("/api/v1/orgs/tree");
      setOrgs(flattenOrgs(result.items));
    } catch {
      setOrgs([]);
      setOrgError(true);
    }
  }, []);
  useEffect(() => {
    void loadOrgs();
  }, [loadOrgs]);

  // 换了承办组织，受众回到「就是本组织」——上一次选的下级可能已经不在这一支里了。
  useEffect(() => {
    setAudienceIds(organizationId ? [organizationId] : []);
  }, [organizationId]);

  const orgName = (id: string) =>
    orgs.find((node) => node.id === id)?.name ??
    items.find((row) => row.organization.id === id)?.organization.name ??
    id;
  const audienceChoices = organizationId ? subtreeOf(orgs, organizationId) : [];
  const holdsMembership =
    orgs.find((node) => node.id === organizationId)?.kind?.canHoldMembership ?? true;
  const quotaTargets = audienceIds.filter(Boolean);

  const open = async (id: string) => {
    const request = ++detailRequest.current;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setDetailId(id);
    setRescheduling(false);
    setSelected(null);
    setDetailLoading(true);
    setDetailError(null);
    setFailure(null);
    setCancelling(false);
    setPool(null);
    setQuotaOrg("");
    try {
      const result = await api<ActivityDetail>(`/api/v1/activities/${encodeURIComponent(id)}`);
      if (request === detailRequest.current) setSelected(result);
    } catch (caught: unknown) {
      if (request === detailRequest.current) setDetailError(reasonText(caught));
    } finally {
      if (request === detailRequest.current) setDetailLoading(false);
    }
  };
  useEffect(() => {
    const id = params.get("activity");
    if (id && params.get("mode") !== "create") void open(id);
    return () => {
      ++detailRequest.current;
    };
  }, []);

  const resetDraft = () => {
    draftCommand.current = null;
    enrollClosesEdited.current = false;
    openEnrollClosesAt.current = null;
    setForm({
      title: "",
      category: "会议",
      summary: "",
      body: "",
      location: "",
      organizerLabel: "市委会办公室",
      contactLabel: "",
      capacity: 40,
      agenda: "",
      notes: "",
      ...defaultWindow()
    });
    setAudienceBasis("MEMBERSHIP_TREE");
    setEnrollmentMode("OPEN");
    setAudienceIds(organizationId ? [organizationId] : []);
    setSeats({});
    setDirty(false);
    setFailure(null);
    setActiveSection("1");
  };
  const startCreate = () => {
    resetDraft();
    setMessage("");
    setCreating(true);
    setDetailId(null);
    setRescheduling(false);
    setSelected(null);
    window.scrollTo({ top: 0 });
  };
  const leaveCreate = () =>
    requestLeave(() => {
      setCreating(false);
      resetDraft();
      window.scrollTo({ top: 0 });
    });
  useEffect(() => {
    if (creating) titleRef.current?.focus();
  }, [creating]);

  const lines = (value: string): string[] =>
    value
      .split("\n")
      .map((row) => row.trim())
      .filter(Boolean);

  // 日程一行一条，「时间｜事项」。让人写两个数组太反人类，也容易写歪。
  const agendaRows = (value: string) =>
    lines(value).map((row) => {
      const [time, ...rest] = row.split(/[｜|]/);
      return { time: (time ?? "").trim(), item: rest.join("|").trim() };
    });

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    if (working) return;
    const formElement = formRef.current;
    if (
      new Date(form.endAt) <= new Date(form.startAt) ||
      new Date(form.enrollClosesAt) <= new Date(form.enrollOpensAt)
    ) {
      const label = new Date(form.endAt) <= new Date(form.startAt) ? "结束" : "报名截止";
      const input = formElement?.querySelector<HTMLInputElement>(
        label === "结束" ? '[name="endAt"]' : '[name="enrollClosesAt"]'
      );
      input?.setCustomValidity(
        label === "结束" ? "活动结束时间须晚于开始时间。" : "报名截止时间须晚于报名开始时间。"
      );
      input?.reportValidity();
      return;
    }
    setWorking(true);
    setFailure(null);
    setMessage("");
    try {
      const payload = {
        organizationId,
        title: form.title,
        category: form.category,
        summary: form.summary,
        body: lines(form.body),
        ...(form.agenda.trim() ? { agenda: agendaRows(form.agenda) } : {}),
        ...(form.notes.trim() ? { notes: lines(form.notes) } : {}),
        location: form.location,
        organizerLabel: form.organizerLabel,
        contactLabel: form.contactLabel,
        startAt: toIso(form.startAt),
        endAt: toIso(form.endAt),
        enrollOpensAt: toIso(form.enrollOpensAt),
        enrollClosesAt: toIso(form.enrollClosesAt),
        capacity: Number(form.capacity),
        audienceOrganizationIds: quotaTargets,
        audienceBasis,
        enrollmentMode,
        // 名额逐个组织填，一个受众组织一格——系统不替谁算分配比例。
        ...(enrollmentMode === "DELEGATED_QUOTA"
          ? {
              quotas: quotaTargets.map((id) => ({
                organizationId: id,
                seats: seats[id] ?? 1
              }))
            }
          : {})
      };
      const fingerprint = JSON.stringify(payload);
      if (draftCommand.current?.fingerprint !== fingerprint) {
        draftCommand.current = { fingerprint, id: commandId() };
      }
      const created = await api<ActivityDetail>("/api/v1/activities", {
        method: "POST",
        body: { commandId: draftCommand.current.id, ...payload }
      });
      setSelected(created);
      setDetailId(created.id);
      setDetailError(null);
      setCreating(false);
      setDirty(false);
      setFilter("DRAFT");
      setMessage(`已存为草稿：${created.publicRef}。发布之后会员端才看得到。`);
      await load("DRAFT");
    } catch (caught: unknown) {
      setFailure(reasonText(caught));
    } finally {
      setWorking(false);
    }
  };

  const openPool = async (targetOrganizationId: string) => {
    setFailure(null);
    setPick("");
    setQuotaOrg(targetOrganizationId);
    try {
      setPool(
        await api<Nominatable>(
          `/api/v1/activities/${selected?.id}/nominatable?organizationId=${encodeURIComponent(targetOrganizationId)}`
        )
      );
    } catch (caught: unknown) {
      setPool(null);
      setFailure(reasonText(caught));
    }
  };

  const nominateAct = async (
    path: "nominations" | "nominations/withdraw",
    personId: string,
    done: string
  ) => {
    if (!selected || working) return;
    setWorking(true);
    setFailure(null);
    try {
      const next = await api<ActivityDetail>(`/api/v1/activities/${selected.id}/${path}`, {
        method: "POST",
        body: { commandId: commandId(), personId }
      });
      setSelected(next);
      setMessage(done);
      if (quotaOrg) await openPool(quotaOrg);
      await load();
    } catch (caught: unknown) {
      setFailure(reasonText(caught));
    } finally {
      setWorking(false);
    }
  };

  const nominate = (personId: string) =>
    nominateAct("nominations", personId, "已报送，可在名额对账中核对。");
  const withdraw = (personId: string) =>
    nominateAct("nominations/withdraw", personId, "已撤回，名额已归还本组织。");

  const act = async (action: "publish" | "cancel", body: Record<string, unknown> = {}) => {
    if (!selected || working) return;
    setWorking(true);
    setFailure(null);
    try {
      const next = await api<ActivityDetail>(`/api/v1/activities/${selected.id}/${action}`, {
        method: "POST",
        body: { commandId: commandId(), expectedVersion: selected.version, ...body }
      });
      setSelected(next);
      setCancelling(false);
      setCancelReason("");
      setMessage(
        action === "publish"
          ? next.enrollmentMode === "DELEGATED_QUOTA"
            ? "已发布，基层负责人可在报名时间内报送名单。"
            : "已发布，受众会员可在报名时间内报名。"
          : "已取消，理由会随活动一起留在记录里。"
      );
      await load();
    } catch (caught: unknown) {
      setFailure(reasonText(caught));
    } finally {
      setWorking(false);
    }
  };

  const visibleItems = items.filter((item) =>
    `${item.title} ${item.publicRef} ${item.organization.name}`
      .toLocaleLowerCase()
      .includes(keyword.trim().toLocaleLowerCase())
  );

  return (
    <Desk wide>
      <div className="workspace">
        <header className="workspace-heading">
          <div>
            <div className="workspace-eyebrow">会务工作</div>
            <h1 ref={titleRef} tabIndex={-1} className="workspace-title">
              {creating ? "新建活动" : "活动管理"}
            </h1>
            <p className="workspace-subtitle">
              {creating
                ? "从组织与受众开始，准备好一场活动。"
                : "从一份草稿开始，把安排、名额与参加记录放在一起。"}
            </p>
          </div>
          <div className="workspace-actions">
            {creating ? (
              <Button type="button" variant="ghost" disabled={working} onClick={leaveCreate}>
                <ArrowLeft className="size-4" />
                返回列表
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="刷新活动列表"
                  disabled={listLoading}
                  onClick={() => void load()}
                >
                  <RefreshCw className="size-4" />
                </Button>
                <Button onClick={startCreate}>
                  <Plus className="size-4" />
                  新建活动
                </Button>
              </>
            )}
          </div>
        </header>
        {message && !detailId ? (
          <div role="status">
            <Banner tone="brand">{message}</Banner>
          </div>
        ) : null}
        {creating ? (
          <>
            {orgError ? (
              <div className="workspace-error" role="alert">
                <span>暂时读不到组织范围，请重新读取后再保存。</span>
                <Button variant="ghost" onClick={() => void loadOrgs()}>
                  重新读取
                </Button>
              </div>
            ) : null}
            <div className="activity-editor">
              <nav className="activity-editor__nav" aria-label="活动填写步骤">
                <p>准备一场活动</p>
                {SECTIONS.map((section) => (
                  <a
                    key={section.id}
                    href={`#activity-section-${section.id}`}
                    aria-current={activeSection === section.id ? "step" : undefined}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <span>0{section.id}</span>
                    {section.label}
                  </a>
                ))}
              </nav>
              <form
                ref={formRef}
                id="activity-create"
                className="activity-editor__form"
                onSubmit={create}
                onChangeCapture={() => setDirty(true)}
                onInputCapture={(event) => {
                  if (event.target instanceof HTMLInputElement) event.target.setCustomValidity("");
                }}
              >
                <fieldset disabled={working} className="contents">
                  <FormSection
                    id="activity-section-1"
                    step="1"
                    title="承办组织"
                    description="选择负责组织本次活动的单位。受众可选本组织及其下级。"
                  >
                    <FieldGrid>
                      <Field label="承办组织" wide>
                        <SelectField
                          value={organizationId}
                          onChange={(next) => {
                            setOrganizationId(next);
                            setDirty(true);
                          }}
                          placeholder="请选择"
                          options={scopes.map((id) => ({ value: id, label: orgName(id) }))}
                        />
                      </Field>
                    </FieldGrid>
                  </FormSection>

                  <FormSection
                    id="activity-section-2"
                    step="2"
                    title="受众范围"
                    description="选择面向哪些组织，以及按会籍还是现届任职确定参加范围。"
                  >
                    <FieldGrid>
                      <Field label="受众来源" wide>
                        <SelectField
                          value={audienceBasis}
                          onChange={(next) => (
                            setDirty(true),
                            setAudienceBasis(next as typeof audienceBasis)
                          )}
                          placeholder="请选择"
                          options={[
                            {
                              value: "MEMBERSHIP_TREE",
                              label: "按会籍：受众组织及其下级的在册会员"
                            },
                            {
                              value: "APPOINTMENT_BODY",
                              label: "按现届任职：这些机构现届的在任委员"
                            }
                          ]}
                        />
                      </Field>
                      <Field
                        label="受众组织"
                        wide
                        hint={
                          audienceBasis === "MEMBERSHIP_TREE"
                            ? "按会籍算受众时会展开到下级：勾上一个组织，等于把它和它下面各支部的在册会员都算进来。"
                            : "按现届任职算受众时不展开下级：说的就是勾中的这几个机构本身。"
                        }
                      >
                        <CheckList
                          options={audienceChoices.map((node) => ({
                            id: node.id,
                            label: node.name,
                            note: node.id === organizationId ? "本组织" : undefined
                          }))}
                          value={audienceIds}
                          onChange={(next) => {
                            setAudienceIds(next);
                            setDirty(true);
                          }}
                          emptyText="先选承办组织。"
                          quickActions={
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => (
                                  setDirty(true),
                                  setAudienceIds(organizationId ? [organizationId] : [])
                                )}
                              >
                                只选本组织
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => (
                                  setDirty(true),
                                  setAudienceIds(audienceChoices.map((node) => node.id))
                                )}
                              >
                                全选下级
                              </Button>
                            </>
                          }
                        />
                      </Field>
                    </FieldGrid>
                    {audienceBasis === "MEMBERSHIP_TREE" && !holdsMembership ? (
                      <Note>
                        本组织的会籍受众来自下级在册会员。如需面向本机构现届委员，请选择「按现届任职」。
                      </Note>
                    ) : null}
                  </FormSection>

                  <FormSection
                    id="activity-section-3"
                    step="3"
                    title="报名设置"
                    description="选择本人报名或基层报送；具体开放时间在下方设置。"
                  >
                    <FieldGrid>
                      <Field label="报名模式" wide>
                        <SelectField
                          value={enrollmentMode}
                          onChange={(next) => {
                            setDirty(true);
                            if (!enrollClosesEdited.current && next !== enrollmentMode) {
                              if (next === "DELEGATED_QUOTA") {
                                openEnrollClosesAt.current = form.enrollClosesAt;
                                setForm({ ...form, enrollClosesAt: form.endAt });
                              } else if (openEnrollClosesAt.current !== null) {
                                setForm({ ...form, enrollClosesAt: openEnrollClosesAt.current });
                              }
                            }
                            setEnrollmentMode(next as typeof enrollmentMode);
                          }}
                          placeholder="请选择"
                          options={[
                            { value: "OPEN", label: "本人自主报名（满额排候补）" },
                            {
                              value: "DELEGATED_QUOTA",
                              label: "分名额到基层（由负责人报送名单）"
                            }
                          ]}
                        />
                      </Field>
                      <Field label="全场名额">
                        <Input
                          type="number"
                          min={1}
                          value={form.capacity}
                          onChange={(event) =>
                            setForm({ ...form, capacity: Number(event.target.value) })
                          }
                          required
                        />
                      </Field>
                      <Field label="类别">
                        <Input
                          value={form.category}
                          onChange={(event) => setForm({ ...form, category: event.target.value })}
                          required
                        />
                      </Field>
                    </FieldGrid>
                    {enrollmentMode === "DELEGATED_QUOTA" ? (
                      <>
                        <Note>
                          请逐个组织填写名额。基层只能在所分配名额内报送，<strong>不排候补</strong>
                          。
                        </Note>
                        <div className="mt-4">
                          <FieldGrid>
                            {quotaTargets.map((id) => (
                              <Field key={id} label={`${orgName(id)} 的名额`}>
                                <Input
                                  type="number"
                                  min={1}
                                  value={seats[id] ?? 1}
                                  onChange={(event) =>
                                    setSeats({ ...seats, [id]: Number(event.target.value) })
                                  }
                                  required
                                />
                              </Field>
                            ))}
                          </FieldGrid>
                        </div>
                      </>
                    ) : null}
                  </FormSection>

                  <FormSection id="activity-section-4" step="4" title="活动内容">
                    <FieldGrid>
                      <Field label="标题" wide>
                        <Input
                          value={form.title}
                          minLength={4}
                          maxLength={120}
                          onChange={(event) => setForm({ ...form, title: event.target.value })}
                          required
                        />
                      </Field>
                      <Field label="摘要" wide>
                        <Textarea
                          value={form.summary}
                          minLength={4}
                          maxLength={400}
                          rows={2}
                          onChange={(event) => setForm({ ...form, summary: event.target.value })}
                          required
                        />
                      </Field>
                      <Field label="说明" wide hint="一行一段。">
                        <Textarea
                          value={form.body}
                          rows={5}
                          onChange={(event) => setForm({ ...form, body: event.target.value })}
                          required
                        />
                      </Field>
                      <Field label="日程" hint="一行一条，「时间｜事项」。">
                        <Textarea
                          value={form.agenda}
                          rows={3}
                          placeholder={"09:00｜集合出发\n10:00｜实地走访"}
                          onChange={(event) => setForm({ ...form, agenda: event.target.value })}
                        />
                      </Field>
                      <Field label="注意事项" hint="一行一条。">
                        <Textarea
                          value={form.notes}
                          rows={3}
                          onChange={(event) => setForm({ ...form, notes: event.target.value })}
                        />
                      </Field>
                      <Field label="地点" wide>
                        <Input
                          value={form.location}
                          onChange={(event) => setForm({ ...form, location: event.target.value })}
                          required
                        />
                      </Field>
                      <Field label="主办单位显示名称">
                        <Input
                          value={form.organizerLabel}
                          onChange={(event) =>
                            setForm({ ...form, organizerLabel: event.target.value })
                          }
                          required
                        />
                      </Field>
                      <Field label="联系说明">
                        <Input
                          value={form.contactLabel}
                          onChange={(event) =>
                            setForm({ ...form, contactLabel: event.target.value })
                          }
                          required
                        />
                      </Field>
                    </FieldGrid>
                  </FormSection>

                  <FormSection id="activity-section-5" step="5" title="活动时间">
                    <FieldGrid>
                      <Field label="开始">
                        <Input
                          type="datetime-local"
                          value={form.startAt}
                          onChange={(event) => setForm({ ...form, startAt: event.target.value })}
                          required
                        />
                      </Field>
                      <Field label="结束">
                        <Input
                          type="datetime-local"
                          name="endAt"
                          value={form.endAt}
                          onChange={(event) => {
                            const endAt = event.target.value;
                            setForm({
                              ...form,
                              endAt,
                              ...(enrollmentMode === "DELEGATED_QUOTA" && !enrollClosesEdited.current
                                ? { enrollClosesAt: endAt }
                                : {})
                            });
                          }}
                          required
                        />
                      </Field>
                      <Field label="报名开始">
                        <Input
                          type="datetime-local"
                          value={form.enrollOpensAt}
                          onChange={(event) =>
                            setForm({ ...form, enrollOpensAt: event.target.value })
                          }
                          required
                        />
                      </Field>
                      <Field
                        label={enrollmentMode === "DELEGATED_QUOTA" ? "报送截止" : "报名截止"}
                        hint={enrollmentMode === "DELEGATED_QUOTA"
                          ? "名额制默认到活动结束，现场来了没报上的人可以当场补报"
                          : undefined}
                      >
                        <Input
                          type="datetime-local"
                          name="enrollClosesAt"
                          value={form.enrollClosesAt}
                          onChange={(event) => {
                            enrollClosesEdited.current = true;
                            setForm({ ...form, enrollClosesAt: event.target.value });
                          }}
                          required
                        />
                      </Field>
                    </FieldGrid>
                  </FormSection>
                </fieldset>
                {failure ? (
                  <div className="activity-editor__error workspace-error" role="alert">
                    {failure}
                  </div>
                ) : null}
                <footer className="activity-editor__footer">
                  <p>
                    <strong>{working ? "正在保存…" : dirty ? "有未保存的内容" : "尚未保存"}</strong>
                    <br />
                    {quotaTargets.length === 0
                      ? "请至少选择一个受众组织。"
                      : "存为草稿后，可核对内容再发布。"}
                  </p>
                  <div className="workspace-actions">
                    <Button type="button" variant="ghost" disabled={working} onClick={leaveCreate}>
                      返回列表
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        working ||
                        !orgs.some((org) => org.id === organizationId) ||
                        orgError ||
                        quotaTargets.length === 0
                      }
                    >
                      {working ? "正在保存…" : "存为草稿"}
                    </Button>
                  </div>
                </footer>
              </form>
            </div>
          </>
        ) : (
          <>
            <Link className="mb-4 inline-block text-sm text-primary underline" href="/activities/ledger">查看活动台账</Link>
            <div className="workspace-tabs" aria-label="活动状态">
              {[
                { value: "PUBLISHED", label: "已发布" },
                { value: "DRAFT", label: "草稿" },
                { value: "CANCELLED", label: "已取消" },
                { value: "CLOSED", label: "已结项" },
                { value: "", label: "全部" }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={filter === option.value}
                  onClick={() => setFilter(option.value as ActivityStatus | "")}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="workspace-toolbar">
              <div className="workspace-search">
                <Search aria-hidden />
                <Input
                  aria-label="搜索本次读取的活动"
                  placeholder="搜索活动名称、编号或组织"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                />
              </div>
              <span className="workspace-count">
                {listLoading
                  ? "正在读取…"
                  : listError
                    ? "读取未完成"
                    : `本次读取 ${items.length} 场${keyword ? ` · 匹配 ${visibleItems.length} 场` : ""}`}
              </span>
            </div>
            {listLoading ? (
              <div className="workspace-loading" role="status" aria-label="正在读取活动">
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </div>
            ) : listError ? (
              <div className="workspace-empty" role="alert">
                <RefreshCw aria-hidden />
                <strong>暂时读不到活动</strong>
                <p>{listError}</p>
                <Button variant="outline" onClick={() => void load()}>
                  重新读取
                </Button>
              </div>
            ) : visibleItems.length === 0 ? (
              <div className="workspace-empty">
                <CalendarDays aria-hidden />
                <strong>{keyword ? "没有匹配的活动" : "这个状态下暂时没有活动"}</strong>
                <p>
                  {keyword
                    ? "换一个关键词，或清除搜索条件。搜索仅覆盖本次读取的记录。"
                    : "你可以查看其他状态，或从草稿开始准备一场活动。"}
                </p>
                {keyword ? (
                  <Button variant="outline" onClick={() => setKeyword("")}>
                    清除搜索
                  </Button>
                ) : (
                  <Button variant="outline" onClick={startCreate}>
                    <FilePlus2 className="size-4" />
                    新建活动
                  </Button>
                )}
              </div>
            ) : (
              <div className="activity-table-wrap">
                <table className="activity-table">
                  <caption className="sr-only">授权范围内的活动；选择名称查看详情</caption>
                  <thead>
                    <tr>
                      <th scope="col">活动 / 承办组织</th>
                      <th scope="col">状态</th>
                      <th scope="col">活动时间</th>
                      <th scope="col">报名情况</th>
                      <th scope="col">
                        <span className="sr-only">操作</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <button
                            type="button"
                            className="activity-record"
                            onClick={() => void open(item.id)}
                            aria-label={`查看活动：${item.title}`}
                          >
                            <span className="activity-record__icon">
                              <CalendarDays className="size-4" aria-hidden />
                            </span>
                            <span>
                              {item.title}
                              <span className="activity-record__meta">
                                {item.organization.name} · {item.publicRef}
                              </span>
                            </span>
                          </button>
                        </td>
                        <td>
                          <StatusTag tone={STATUS_TONE[item.status]}>
                            {STATUS_LABEL[item.status]}
                          </StatusTag>
                        </td>
                        <td>
                          <time dateTime={item.startAt}>
                            {new Intl.DateTimeFormat("zh-CN", {
                              year: "numeric",
                              month: "long",
                              day: "numeric"
                            }).format(new Date(item.startAt))}
                          </time>
                          <span className="activity-table__sub">
                            {new Intl.DateTimeFormat("zh-CN", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false
                            }).format(new Date(item.startAt))}{" "}
                            · {item.category}
                          </span>
                        </td>
                        <td>
                          <span className="activity-table__count">
                            {item.status === "DRAFT"
                              ? "发布后开放"
                              : item.enrollmentMode === "DELEGATED_QUOTA"
                                ? `${item.pendingSeats ?? "—"} 席待报`
                                : `${item.registeredCount} / ${item.capacity} 人`}
                          </span>
                          <span className="activity-table__sub">
                            {item.enrollmentMode === "DELEGATED_QUOTA" ? "基层报送" : "自主报名"}
                          </span>
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`打开 ${item.title}`}
                            onClick={() => void open(item.id)}
                          >
                            <ArrowRight className="size-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!listLoading && !listError ? (
              <p className="home-source-note">
                按开始时间倒序，最多读取 100 场；搜索仅在本次记录中匹配。
                {readAt ? `读取于 ${readAt}。` : ""}
              </p>
            ) : null}
          </>
        )}
        <Sheet
          open={Boolean(detailId)}
          onOpenChange={(openState) => {
            if (!openState) {
              ++detailRequest.current;
              setDetailId(null);
              setRescheduling(false);
              setSelected(null);
              setFailure(null);
            }
          }}
        >
          <SheetContent
            className="activity-sheet workspace"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              if (opener.current?.isConnected) opener.current.focus();
              else titleRef.current?.focus();
            }}
          >
            <SheetHeader>
              <SheetTitle>{selected?.title ?? "活动详情"}</SheetTitle>
              <SheetDescription>
                {selected
                  ? `${selected.publicRef} · 更新于 ${formatTime(selected.updatedAt)}`
                  : "查看活动安排、名额与参加记录"}
              </SheetDescription>
            </SheetHeader>
            <div className="activity-sheet__body">
              {message ? (
                <div role="status">
                  <Banner tone="brand">{message}</Banner>
                </div>
              ) : null}
              {failure ? (
                <div role="alert">
                  <Banner tone="danger">{failure}</Banner>
                </div>
              ) : null}
              {detailLoading ? (
                <div className="workspace-loading" role="status" aria-label="正在读取活动详情">
                  <Skeleton />
                  <Skeleton />
                </div>
              ) : detailError ? (
                <div className="workspace-empty" role="alert">
                  <strong>活动详情未能读取</strong>
                  <p>{detailError}</p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (detailId) void open(detailId);
                    }}
                  >
                    重新读取
                  </Button>
                </div>
              ) : selected ? (
                <>
                  <DeskCard
                    title="活动信息"
                    description={`${selected.organization.name} · 版本 ${selected.version}`}
                    actions={
                      <>
                        <StatusTag tone={STATUS_TONE[selected.status]}>
                          {STATUS_LABEL[selected.status]}
                        </StatusTag>
                        {selected.status === "DRAFT" ? (
                          <Button
                            variant="default"
                            size="sm"
                            disabled={working}
                            onClick={() => void act("publish")}
                          >
                            发布
                          </Button>
                        ) : null}
                        {selected.status === "PUBLISHED" && hasRole("ACTIVITY_COORDINATOR") && <Button variant="outline" size="sm" disabled={working} onClick={() => setRescheduling(true)}>改期</Button>}
                        {selected.status === "PUBLISHED" ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={working}
                            onClick={() => setCancelling(true)}
                          >
                            取消活动
                          </Button>
                        ) : null}
                      </>
                    }
                  >
                    {rescheduling && <ActivityReschedule<ActivityDetail> key={`${selected.id}:${selected.version}`} activity={selected} onCancel={() => setRescheduling(false)} onSaved={(next) => { setSelected(next); setRescheduling(false); setMessage("已改期，名册与签到保留。"); void load(); }} />}
                    <Facts>
                      <Fact label="时间">
                        {formatTime(selected.startAt)} — {formatTime(selected.endAt)}
                      </Fact>
                      <Fact label="地点">{selected.location}</Fact>
                      <Fact label="报名方式">
                        {selected.enrollmentMode === "DELEGATED_QUOTA"
                          ? "分名额到基层，由负责人报送名单"
                          : "本人自主报名，满额排候补"}
                      </Fact>
                      <Fact label="人数">
                        报名 {selected.registeredCount} / {selected.capacity}
                        {selected.waitlistedCount > 0 ? ` · 候补 ${selected.waitlistedCount}` : ""}
                        {selected.excusedCount > 0 ? ` · 请假 ${selected.excusedCount}` : ""}
                        {` · 签到 ${selected.attendedCount}`}
                      </Fact>
                      <Fact label="受众">{selected.audienceCount} 人</Fact>
                    </Facts>
                    {cancelling ? (
                      <ConfirmPanel
                        title="取消这场活动？"
                        consequence="取消之后会员端不再显示报名入口，已报名的人会看到取消。理由会随活动一起留在记录里，作为后续核对依据。"
                        onCancel={() => setCancelling(false)}
                        onConfirm={() => void act("cancel", { reason: cancelReason.trim() })}
                        confirmLabel="确认取消这场活动"
                        busy={working}
                        confirmDisabled={cancelReason.trim().length === 0}
                      >
                        <Field label="取消理由" hint="会留在记录里，会员端看得到。">
                          <Input
                            value={cancelReason}
                            onChange={(event) => setCancelReason(event.target.value)}
                          />
                        </Field>
                      </ConfirmPanel>
                    ) : null}
                  </DeskCard>

                  {selected.status === "PUBLISHED" ? (
                    <OnSiteCheckIn activity={selected} onChanged={setSelected} />
                  ) : null}

                  {selected.archivePermissions?.read ? <ActivityArtifacts key={selected.id} activity={selected} editable={selected.archivePermissions.manage} onBusy={setWorking} /> : <Note>材料与纪要仅供承办组织授权范围内查阅。</Note>}
                  <ActivityCloseout activity={selected} editable={selected.archivePermissions?.manage === true} disabled={working} onBusy={setWorking} onSaved={async () => { const next = await api<ActivityDetail>(`/api/v1/activities/${selected.id}`); setSelected(next); await load(); }} />
                  <DeskCard title="活动内容" description={selected.summary}>
                    <div className="activity-sheet__document">
                      {selected.body.map((paragraph, index) => (
                        <p key={index} className="mb-4 last:mb-0">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    {selected.notes.length > 0 ? <Note>{selected.notes.join("；")}</Note> : null}
                  </DeskCard>
                  {(selected.quotas ?? []).length > 0 ? (
                    <DeskCard title="名额对账" description="仅显示你有权查看的组织名额与报送情况。">
                      <RecordList>
                        {selected.quotas.map((cell) => (
                          <RecordRow
                            key={cell.organizationId}
                            title={cell.organizationName}
                            meta={
                              <>
                                已报 {cell.nominated} / {cell.seats}
                                {cell.excused > 0 ? ` · 其中请假 ${cell.excused}` : ""}
                              </>
                            }
                            actions={
                              <>
                                {cell.nominated >= cell.seats ? (
                                  <StatusTag tone="neutral">已报满</StatusTag>
                                ) : (
                                  <StatusTag tone="warning">
                                    空 {cell.seats - cell.nominated} 席
                                  </StatusTag>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={working || selected.status !== "PUBLISHED"}
                                  onClick={() => void openPool(cell.organizationId)}
                                >
                                  报送
                                </Button>
                              </>
                            }
                          />
                        ))}
                      </RecordList>
                    </DeskCard>
                  ) : null}

                  {pool && quotaOrg && selected.status === "PUBLISHED" ? (
                    <DeskCard
                      title={`报送 · ${orgName(quotaOrg)}`}
                      description={`已报送 ${pool.nominated} / ${pool.seats} 席，报满后不排候补。`}
                      actions={
                        <Button size="sm" variant="ghost" onClick={() => setPool(null)}>
                          收起
                        </Button>
                      }
                    >
                      <FieldGrid>
                        <Field label="从本组织在册会员里选一位" wide>
                          <SelectField
                            value={pick}
                            onChange={setPick}
                            placeholder="请选择"
                            options={pool.items
                              .filter(
                                (row) => row.status !== "REGISTERED" && row.status !== "EXCUSED"
                              )
                              .map((row) => ({
                                value: row.personId,
                                label: row.displayName
                              }))}
                          />
                        </Field>
                      </FieldGrid>
                      <div className="mt-4">
                        <ButtonRow>
                          <Button
                            variant="default"
                            disabled={working || !pick}
                            onClick={() => void nominate(pick)}
                          >
                            确认报送
                          </Button>
                        </ButtonRow>
                      </div>
                      <RecordList>
                        {pool.items
                          .filter((row) => row.status === "REGISTERED" || row.status === "EXCUSED")
                          .map((row) => (
                            <RecordRow
                              key={row.personId}
                              title={row.displayName}
                              meta={
                                row.status === "EXCUSED"
                                  ? "已报送 · 本人已请假（名额不退）"
                                  : "已报送"
                              }
                              actions={
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  disabled={working}
                                  onClick={() => void withdraw(row.personId)}
                                >
                                  撤回
                                </Button>
                              }
                            />
                          ))}
                      </RecordList>
                      <Note>如需联系会员，请进入联系名册，按授权范围查看联系方式。</Note>
                    </DeskCard>
                  ) : null}

                  {(selected.roster ?? []).length > 0 ? (
                    <DeskCard
                      title="名册"
                      description="只用于组织工作。请假是名册事实，不计次、不排名、不进任何评价。"
                    >
                      <RecordList>
                        {selected.roster.map((row) => (
                          <RecordRow
                            key={row.personId}
                            title={row.displayName}
                            meta={
                              [
                                row.checkedInAt ? "已到" : null,
                                row.excuseReason ? `理由：${row.excuseReason}` : null
                              ]
                                .filter(Boolean)
                                .join(" · ") || undefined
                            }
                            actions={
                              <>
                                <StatusTag tone={row.checkedInAt ? "success" : ROSTER_TONE[row.status] ?? "neutral"}>
                                  {row.checkedInAt ? "已到" : ROSTER_LABEL[row.status] ?? row.status}
                                </StatusTag>
                                {hasRole("ACTIVITY_COORDINATOR") && selected.status === "PUBLISHED" && row.status === "REGISTERED" && !row.checkedInAt && <ConfirmedCommand<ActivityDetail> label="登记到会" title={`代 ${row.displayName} 登记到会，留痕`} consequence="只登记到会事实，操作人员与办理时间会留在记录中。" path={`/api/v1/activities/${selected.id}/attendance`} body={{ personId: row.personId }} disabled={working} onSaved={(next) => { setSelected(next); setMessage("已登记到会，可在名册核对。"); void load(); }} />}
                              </>
                            }
                          />
                        ))}
                      </RecordList>
                    </DeskCard>
                  ) : null}
                </>
              ) : null}
            </div>
          </SheetContent>
        </Sheet>
        {dialog}
      </div>
    </Desk>
  );
}

// 现场口令（重构方案 §7.1）：管签到的人在会场上把四位数亮在屏幕上，每分钟一换，
// 会员输入后才算到会。口令按服务端给的 validUntil 到点再读，不轮询。
function OnSiteCheckIn({
  activity,
  onChanged
}: {
  activity: ActivityDetail;
  onChanged: (next: ActivityDetail) => void;
}) {
  const onSite = activity.checkInMode === "ON_SITE_CODE";
  const [code, setCode] = useState<{ code: string; validUntil: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!onSite) {
      setCode(null);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const read = async () => {
      try {
        const next = await api<{ code: string; validUntil: string }>(
          `/api/v1/activities/${activity.id}/on-site-code`
        );
        if (stopped) return;
        setCode(next);
        setFailure(null);
        timer = setTimeout(
          () => void read(),
          Math.max(1_000, Date.parse(next.validUntil) - Date.now() + 300)
        );
      } catch (caught: unknown) {
        if (!stopped) {
          setCode(null);
          setFailure(reasonText(caught));
        }
      }
    };
    void read();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [onSite, activity.id]);

  const switchMode = async () => {
    setWorking(true);
    setFailure(null);
    try {
      onChanged(
        await api<ActivityDetail>(`/api/v1/activities/${activity.id}/check-in-mode`, {
          method: "POST",
          body: { commandId: commandId(), mode: onSite ? "SELF" : "ON_SITE_CODE" }
        })
      );
      setConfirming(false);
    } catch (caught: unknown) {
      setFailure(reasonText(caught));
    } finally {
      setWorking(false);
    }
  };

  return (
    <DeskCard
      title="现场签到"
      description={
        onSite
          ? "会员签到时须输入下面的四位口令。请把这一块亮在会场屏幕上，或念给到场的会员。"
          : "会员在签到时间内点一下即可签到。要核对是否在会场，改用现场口令。"
      }
      actions={
        <Button variant="outline" size="sm" disabled={working} onClick={() => setConfirming(true)}>
          {onSite ? "改回自助签到" : "改用现场口令"}
        </Button>
      }
    >
      {onSite ? (
        <div className="onsite-code" aria-live="polite">
          <span className="onsite-code__value" aria-label="当前现场口令">
            {code?.code ?? "····"}
          </span>
          <span className="onsite-code__hint">
            {code
              ? `${formatTime(code.validUntil)} 换下一个；刚换下的上一个仍然认。`
              : "口令暂时读不出来；可在名册里代人登记到会。"}
          </span>
        </div>
      ) : null}
      {confirming ? (
        <ConfirmPanel
          title={onSite ? "改回自助签到？" : "改用现场口令？"}
          consequence={
            onSite
              ? "改回之后，会员在签到时间内点一下即可签到，不再核对是否在会场。"
              : "改用之后，会员签到须输入会场上亮出的四位数，每分钟一换；没到场的人签不上。"
          }
          onCancel={() => setConfirming(false)}
          onConfirm={() => void switchMode()}
          confirmLabel={onSite ? "改回自助签到" : "改用现场口令"}
          busy={working}
        />
      ) : null}
      {failure ? <Note>{failure}</Note> : null}
    </DeskCard>
  );
}
